/**
 * Linking Helper Utility
 * Generates contextual links for products, ingredients, farms, and categories
 */

interface LinkConfig {
  text: string;
  url: string;
  type: 'product' | 'farm' | 'category' | 'seasonal' | 'recipe' | 'guide' | 'home' | 'current';
}

class LinkingHelper {
  /**
   * Generate product page URL
   */
  getProductUrl(productName: string, location?: string): string {
    const slug = productName.toLowerCase().replace(/\s+/g, '-');
    if (location) {
      const locationSlug = location.toLowerCase().replace(/\s+/g, '-');
      return `/produce/${slug}-${locationSlug}`;
    }
    return `/produce/${slug}`;
  }

  /**
   * Generate search URL with filters
   */
  getSearchUrl(query: string, filters?: { type?: string; city?: string }): string {
    const params = new URLSearchParams();
    params.set('q', query);

    if (filters?.type) params.set('type', filters.type);
    if (filters?.city) params.set('city', filters.city);

    return `/browse?${params.toString()}`;
  }

  /**
   * Generate farm listing URL
   */
  getFarmUrl(slug: string): string {
    return `/listing/${slug}`;
  }

  /**
   * Generate category URL
   */
  getCategoryUrl(category: string): string {
    const slug = category.toLowerCase().replace(/\s+/g, '-');
    return `/category/${slug}`;
  }

  /**
   * Generate seasonal page URL
   */
  getSeasonalUrl(month?: number): string {
    if (month) {
      const monthNames = [
        'january', 'february', 'march', 'april', 'may', 'june',
        'july', 'august', 'september', 'october', 'november', 'december'
      ];
      return `/seasonal/${monthNames[month - 1]}`;
    }
    return '/seasonal';
  }

  /**
   * Generate guide URL
   */
  getGuideUrl(topic: string): string {
    const slug = topic.toLowerCase().replace(/\s+/g, '-');
    return `/guide/${slug}`;
  }

  /**
   * Generate recipe URL
   */
  getRecipeUrl(recipeSlugOrName: string): string {
    const slug = recipeSlugOrName.toLowerCase().replace(/\s+/g, '-');
    return `/recipe/${slug}`;
  }

  /**
   * Generate product search by tag
   */
  getProductTagUrl(tag: string): string {
    return this.getSearchUrl(tag, { type: 'farm' });
  }

  /**
   * Get contextual links for a product
   */
  getProductLinks(productName: string): LinkConfig[] {
    return [
      {
        text: `Find ${productName}`,
        url: this.getSearchUrl(productName),
        type: 'product',
      },
      {
        text: `${productName} Guide`,
        url: this.getProductUrl(productName),
        type: 'guide',
      },
      {
        text: 'Seasonal Calendar',
        url: '/seasonal',
        type: 'seasonal',
      },
    ];
  }

  /**
   * Get related ingredient links
   */
  getIngredientLinks(ingredients: string[]): LinkConfig[] {
    return ingredients.slice(0, 5).map(ingredient => ({
      text: ingredient,
      url: this.getProductUrl(ingredient),
      type: 'product',
    }));
  }

  /**
   * Get category filter links
   */
  getCategoryLinks(): LinkConfig[] {
    const categories = [
      'Organic Farms',
      'U-Pick Farms',
      'Farmers Markets',
      'CSA Programs',
      'Farm Stands',
      'Specialty Farms',
    ];

    return categories.map(cat => ({
      text: cat,
      url: this.getCategoryUrl(cat),
      type: 'category',
    }));
  }

  /**
   * Get location links
   */
  getLocationLinks(): LinkConfig[] {
    const cities = [
      'Fort Myers',
      'Cape Coral',
      'Naples',
      'Bonita Springs',
      'Estero',
      'Sanibel',
    ];

    return cities.map(city => ({
      text: `${city} Farms`,
      url: `/farms/${city.toLowerCase().replace(/\s+/g, '-')}`,
      type: 'farm',
    }));
  }

  /**
   * Generate tag chip data
   */
  generateTagChip(text: string, url: string, color?: string) {
    return {
      text,
      url,
      color: color || '#2D5A27',
    };
  }

  /**
   * Parse product tags and generate links
   */
  parseProductTags(products: any[]): { name: string; url: string }[] {
    const tags: { name: string; url: string }[] = [];

    products.forEach(product => {
      const productName = typeof product === 'string' ? product : product.name || product;
      tags.push({
        name: productName,
        url: this.getProductUrl(productName),
      });
    });

    return tags;
  }

  /**
   * Generate breadcrumb links
   */
  generateBreadcrumbs(path: string[]): LinkConfig[] {
    return path.map((item, index) => {
      const url = index === 0 ? '/' : `/${path.slice(0, index + 1).join('/')}`;
      return {
        text: item,
        url,
        type: 'guide',
      };
    });
  }

  /**
   * Get trending searches
   */
  getTrendingSearches(): string[] {
    return [
      'Organic tomatoes',
      'Fresh strawberries',
      'Local honey',
      'Farm fresh eggs',
      'Seasonal vegetables',
      'U-Pick farms',
      'CSA programs near me',
      'Farmers markets this weekend',
    ];
  }

  /**
   * Generate related content links
   */
  getRelatedContent(currentType: string, currentTopic: string): LinkConfig[] {
    const related: LinkConfig[] = [];

    switch (currentType) {
      case 'product':
        related.push(
          { text: 'Recipes', url: '/browse', type: 'recipe' },
          { text: 'Where to Buy', url: this.getSearchUrl(currentTopic), type: 'farm' },
          { text: 'Seasonal Guide', url: '/seasonal', type: 'seasonal' }
        );
        break;
      case 'farm':
        related.push(
          { text: 'Similar Farms', url: '/browse', type: 'farm' },
          { text: 'Products Available', url: this.getSearchUrl(currentTopic), type: 'product' },
          { text: 'Nearby Markets', url: '/map', type: 'category' }
        );
        break;
      case 'seasonal':
        related.push(
          { text: 'Browse Farms', url: '/browse', type: 'farm' },
          { text: 'Recipe Ideas', url: '/browse', type: 'recipe' },
          { text: 'Shopping Guide', url: this.getGuideUrl('shopping-at-farmers-markets'), type: 'guide' }
        );
        break;
    }

    return related;
  }

  /**
   * Generate recipe archive URL with optional filters
   */
  getRecipeArchiveUrl(filter?: { diet?: string; difficulty?: string; quick?: boolean }): string {
    const params = new URLSearchParams();
    if (filter?.diet) params.append('diet', filter.diet);
    if (filter?.difficulty) params.append('difficulty', filter.difficulty);
    if (filter?.quick) params.append('filter', 'quick');

    const queryString = params.toString();
    return `/recipes${queryString ? '?' + queryString : ''}`;
  }

  /**
   * Generate produce directory URL with optional category filter
   */
  getProduceDirectoryUrl(category?: string): string {
    if (category) {
      return `/produce?category=${category}`;
    }
    return '/produce-directory';
  }

  /**
   * Generate produce filter URL by category
   */
  getProduceFilterUrl(category: 'fruit' | 'vegetable' | 'herb'): string {
    return `/produce?category=${category}`;
  }

  /**
   * Generate seasonal guide archive URL
   */
  getSeasonalGuideArchiveUrl(): string {
    return '/seasonal-guides';
  }

  /**
   * Generate blog category URL
   */
  getBlogCategoryUrl(category: string): string {
    const slug = category.toLowerCase().replace(/\s+/g, '-');
    return `/events?category=${slug}`;
  }

  /**
   * Generate breadcrumbs for recipe pages
   */
  getRecipeBreadcrumbs(recipe: { name: string; cuisine?: string; difficulty?: string }): LinkConfig[] {
    return [
      { text: 'Home', url: '/', type: 'home' },
      { text: 'Recipes', url: '/recipes', type: 'recipe' },
      { text: recipe.name, url: '', type: 'current' }
    ];
  }

  /**
   * Generate breadcrumbs for produce pages
   */
  getProduceBreadcrumbs(produceName: string, category?: string): LinkConfig[] {
    const breadcrumbs: LinkConfig[] = [
      { text: 'Home', url: '/', type: 'home' },
      { text: 'Produce', url: '/produce-directory', type: 'product' }
    ];

    if (category) {
      breadcrumbs.push({
        text: category.charAt(0).toUpperCase() + category.slice(1) + 's',
        url: this.getProduceFilterUrl(category as 'fruit' | 'vegetable' | 'herb'),
        type: 'category'
      });
    }

    breadcrumbs.push({ text: produceName, url: '', type: 'current' });
    return breadcrumbs;
  }
}

export default new LinkingHelper();
